/**
 * English — the second locale.
 *
 * Kept complete rather than deleted. The API, the protocol documents and the operator runbooks are
 * all in English, and somebody cross-referencing a support thread against the interface needs to
 * be able to switch without losing half the screen to fallback French.
 *
 * The key structure mirrors `fr-FR` exactly. A key that exists in one catalogue and not the other
 * is a bug, and `tests/i18n.spec.ts` fails on it rather than leaving it to be discovered by a
 * reader who suddenly sees the other language.
 */
export default {
  app: {
    name: 'oui Observe',
    tagline: 'One console for the whole estate',
  },

  locale: {
    label: 'Language',
    switch: 'Change language',
  },

  theme: {
    system: 'System theme',
    light: 'Light theme',
    dark: 'Dark theme',
    cycle: 'Change theme',
  },

  nav: {
    documentation: 'API documentation',
    home: 'Dashboard',
    homeHint: 'What needs you',
    sections: 'Sections',
    skipToContent: 'Skip to content',
    toggle: 'Toggle navigation',
    assets: 'Assets',
    assetsHint: 'Every kind, one list',
    operations: 'Operations',
    operationsHint: 'In flight and history',
    activity: 'Activity',
    activityHint: 'The pushed audit trail',
    alerts: 'Alerts',
    alertsHint: 'One engine, every kind',
    noPrivilegedChannel:
      'This dashboard holds no privileged channel. Everything it does, the public API does.',
  },

  session: {
    signedIn: 'signed in',
    signOut: 'Sign out',
  },

  common: {
    edit: 'Edit',
    unknown: 'Unknown',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    retry: 'Retry',
    reset: 'Reset',
    clear: 'Clear',
    search: 'Search',
    filter: 'Filter',
    run: 'Run',
    loading: 'Loading...',
    justNow: 'just now',
    ago: '{duration} ago',
    in: 'in {duration}',
    since: 'since {at}',
    on: 'on',
    of: 'of',
    andMore: 'and {count} more',
    copy: 'Copy',
    copied: 'Copied',
    details: 'Details',
    verbatim: 'verbatim',
    selectPlaceholder: '- Select -',
    add: 'Add',
    select: 'Select',
    actions: 'Actions',
  },

  units: {
    megabytes: '{value} MB',
    kilobytes: '{value} kB',
    milliseconds: 'ms',
    seconds: 's',
    days: 'd',
    percent: '%',
    perSecond: '/s',
    hours: 'h',
  },

  duration: {
    day: '{count}d',
    hour: '{count}h',
    minute: '{count}m',
    second: '{count}s',
    underOneSecond: '<1 s',
  },

  kind: {
    server: 'Server',
    site: 'Site',
    domain: 'Domain',
    endpoint: 'Endpoint',
    unknown: 'Unknown kind',
    serverHint: 'Server — a host running an agent',
    siteHint: 'Site — an install with an agent',
    domainHint: 'Domain — registration and expiry only, no agent',
    endpointHint: 'Endpoint — an external probe target, no agent',
    label: 'Kind',
  },

  health: {
    label: 'Health',
    unknown: 'Unknown',
    ok: 'OK',
    warning: 'Warning',
    critical: 'Critical',
    unknownHint: 'Not yet checked — this is not the same as healthy.',
    okHint: 'Every check that ran, passed.',
    warningHint: 'Something needs attention but the asset is serving.',
    criticalHint: 'Something is wrong now.',
  },

  availability: {
    label: 'Availability',
    up: 'Up',
    suspect: 'Suspect',
    down: 'Down',
    recovering: 'Recovering',
    unknown: 'Unknown',
    upHint: 'Reachable from every prober.',
    suspectHint: 'A probe failed. Quorum not reached, so this has not alerted.',
    downHint: 'A quorum of probers agreed, across two cycles.',
    recoveringHint: 'Coming back — probes are passing again but the state has not settled.',
    unknownHint: 'No prober has reached a verdict for this asset yet.',
  },

  severity: {
    debug: 'debug',
    info: 'info',
    warning: 'warning',
    error: 'error',
    critical: 'critical',
    severe: 'severity 3 or above',
    min: 'Minimum severity',
  },

  operationState: {
    queued: 'Queued',
    running: 'Running',
    done: 'Done',
    error: 'Failed',
    blocked: 'Blocked',
    unknown: 'Unknown state',
  },

  eventCategory: {
    auth: 'Authentication',
    user: 'Users',
    content: 'Content',
    extension: 'Extensions',
    form: 'Forms',
    commerce: 'Commerce',
    file: 'Files',
    security: 'Security',
    system: 'System',
    other: 'Other',
    all: 'All categories',
  },

  range: {
    label: 'Range',
    '1h': 'Last hour',
    '24h': 'Last 24 hours',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '13m': 'Last 13 months',
    custom: 'Custom range',
    selected: 'over the selected window',
  },

  auth: {
    signIn: 'Sign in',
    email: 'Email',
    password: 'Password',
    continue: 'Continue',
    secondFactor: 'Second factor',
    signingInAs: 'Signing in as {email}.',
    authenticatorCode: 'Authenticator code',
    securityKey: 'Security key',
    sixDigitCode: 'Six-digit code',
    assertion: 'Assertion',
    codeHelp: 'From your authenticator app.',
    keyHelp: 'Touch your security key; your browser fills this in.',
    verify: 'Verify',
    useAnotherAccount: 'Use a different account',
    mfaMandatory:
      'A second factor is required for every account. This interface holds credentials to every site the agency manages, and it is the most valuable target in the estate.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    reauthTitle: 'Re-authentication required',
    reauthDetail: 'This action is destructive. Confirm your identity to authorise it: {label}.',
    reauthConfirm: 'Confirm my identity',
    enrolmentRequired:
      'No second factor is enrolled on this account. Contact your administrator to enrol one.',
  },

  home: {
    activityVolume: {
      title: 'Activity volume over 24 hours',
    },
    title: 'Dashboard',
    subtitle: 'What needs you, in the order worth asking.',
    documentation: 'Documentation',
    health: {
      title: 'Estate health',
      allWell: 'Nothing is failing.',
    },
    updates: {
      title: 'Updates and security',
      available: 'Updates available',
      vulnerable: 'Vulnerable extensions',
      none: 'Everything is up to date.',
      silent: '{count} asset(s) have an enrolled agent that has never reported.',
    },
    operations: {
      title: 'Operations',
      none: 'No recent operations.',
    },
    activity: {
      title: 'Recent activity',
      none: 'No events received.',
      all: 'The whole activity log',
    },
    agents: {
      title: 'Agents to install',
      intro:
        'Install the agent on a site for extension inventory, updates and the activity log. Each package is built with the core serving this page, so the versions cannot drift apart.',
      download: 'Download',
      unavailable: 'The packages are not available on this deployment.',
      checksum: 'checksum {sum}…',
    },
  },
  assets: {
    // A badge on an archived row. Singular, unlike assets.filters.archived,
    // which labels a filter over many.
    archived: 'Archived',
    title: 'Assets',
    subtitle:
      'Servers, sites, domains and endpoints in one list. Alerting, grouping, permissions, audit and search are written once against {code}, which is why there is no second screen for servers.',
    label: 'Label',
    group: 'Group',
    tags: 'Tags',
    url: 'URL',
    lastSeen: 'Last seen',
    capabilities: 'Capabilities',
    extensions: 'Extensions',
    updates: 'Updates',
    inFlight: 'In flight',
    selectAll: 'Select all',
    selectRow: 'Select this asset',
    empty: 'No assets',
    emptyDetail: 'No asset matches your filters. Widen the search or reset the filters.',
    loadError: 'Could not load assets',
    countLabel: 'assets',
    archive: {
      title: 'Archive',
      action: 'Archive',
      restore: 'Restore',
      confirmOne: 'Archive "{label}"?',
      confirmMany: 'Archive {count} assets?',
      whatHappens:
        'The asset stops being probed and leaves the list. All of its history — events, metrics, incidents — is kept, and it can be restored at any time.',
    },
    form: {
      createTitle: 'New asset',
      createSubtitle: 'Add a site, a server or a domain to monitor.',
      editTitle: 'Edit asset',
      editSubtitle:
        'Label, group and tags. The address is not editable: changing it would make this a different asset, whose history would belong to somebody else.',
      createAction: 'Add asset',
      kind: 'Kind',
      kinds: {
        endpoint: 'Website',
        server: 'Server',
        domain: 'Domain name',
      },
      kindHelp: {
        endpoint:
          'Probed from outside: availability, response time, TLS certificate. Install the agent afterwards for extensions, updates and the activity log.',
        server:
          'A machine. Only the hostname is needed; OS, memory and CPUs stay unknown until its agent enrolls.',
        domain: 'Registration and expiry only. Nothing is probed.',
      },
      noSiteKind:
        'There is no "site" kind to choose: an asset becomes one when its agent enrolls and declares its capabilities. A manifest can only come from an agent.',
      labelPlaceholder: 'Name shown in lists and alerts',
      url: 'Address',
      urlHelp: 'The public address of the site, the one probed every cycle.',
      urlPlaceholder: 'https://example.com/',
      hostname: 'Hostname',
      hostnameHelp: 'The name the machine is reached by.',
      hostnamePlaceholder: 'srv-01.example.com',
      domainName: 'Domain name',
      domainNameHelp: 'No protocol and no trailing slash.',
      domainNamePlaceholder: 'example.com',
      noGroup: 'No group',
      tagsHelp: 'Comma-separated.',
      tagsPlaceholder: 'production, client-x',
    },
    enroll: {
      title: 'Connect an agent',
      intro:
        'A single-use token lets the agent installed on this site enroll. It is valid for a few minutes only.',
      step1: 'Install the oui Observe package on the site.',
      step2: "Open oui Observe in the site's administration.",
      step3: 'Paste the token below into it and submit.',
      mint: 'Generate a token',
      onceWarning:
        'This token is shown once. The core keeps only a hash: once this window closes it cannot be recovered, and you have to generate another.',
      tokenLabel: 'Enrollment token',
      coreLabel: 'Core',
      expires: 'Expires at {at}.',
      whereToPaste:
        "Paste into the site's oui Observe screen. The core address is already filled in there.",
      copy: 'Copy token',
      copied: 'Copied',
    },
    filters: {
      search: 'Search assets...',
      kind: 'Kind',
      health: 'Health',
      availability: 'Availability',
      group: 'Group',
      archived: 'Archived',
      showArchived: 'Show archived assets',
      reset: 'Reset filters',
      allGroups: 'Every group',
      clearCount: 'Clear filters | Clear one filter | Clear {count} filters',
      declaresHint: 'Filter by what an asset declares it can do, not by what it runs.',
      capabilityAria: 'Filter by declared capability id',
    },
    bulk: {
      selected: 'no assets selected | one asset selected | {count} assets selected',
      inCommon: '{count} capabilities in common',
      chooseAction: 'Choose an action...',
      run: 'Run',
      clear: 'Clear selection',
      noCommon:
        'No capability is declared by every selected asset. A bulk action is offered only when all of the targets can actually perform it — the intersection, not the union.',
      offered: 'Offered because every selected asset declares {list}.',
      queued: '{queued} of {total} operations queued',
      onAssets: '{action} on {count} assets',
    },
    loadingList: 'Loading assets',
    tableCaption: 'Monitored assets, with health, product and update state.',
    selectOne: 'Select {label}',
    declares: 'Declares',
    declaresHint: 'How many capabilities this asset declares.',
    inFlightCount: 'no operations in flight | one operation in flight | {count} operations in flight',
    runningCount: 'none running | one running | {count} running',
    extUpdateCount: '{count} ext',
    updateStateUnknownHint: 'This asset does not report update state — that is not the same as being up to date.',
  },

  assetDetail: {
    loading: 'Loading asset',
    loadError: 'Could not load this asset.',
    breadcrumb: 'Assets',
    operations: 'Operations',
    operationsSubtitle: 'Newest first, polled while in flight',
    allOperations: 'All operations for this asset',
    extensions: 'Extensions',
    manifestFetched: 'manifest {relative}',
    manifestFetchedHint: "When the core last fetched this asset's manifest.",
    loadingInventory: 'Loading inventory',
    inventoryUnavailable: 'The inventory is not available.',
    inventoryError: 'Could not load the inventory.',
    recentActivity: 'Recent activity',
    batchSize: 'batch {count}',
    batchSizeHint: 'Maximum events per push, declared by the agent.',
    searchFullLog: 'Search the full log',
    declares: 'declares: {list}',
    capabilitiesUnknown: 'What this asset can do is not known yet.',
    declaredNotReadable: 'Declared but not yet readable',
    declaredNotReadableSubtitle:
      'The asset reports these; the API has no endpoint to read them through',
    declaredNotReadableFooter:
      'This is a gap in the management API, not in the asset. The agent is reporting these capabilities and the operation endpoint accepts them; there is simply no resource to read their state through yet.',
    availability: 'Availability',
    uptime: 'Uptime',
    currentState: 'Current state',
    outages: 'Outages',
    downtime: 'Downtime',
    averageResponse: 'Average response',
    lastChecked: 'Last checked',
    noAvailabilityHistory:
      'No availability history for this window. That is unknown, not 100% and not 0%.',
    metrics: 'Metrics',
    product: 'Product',
    family: 'Family',
    version: 'Version',
    php: 'PHP',
    agent: 'Agent',
    manifest: 'Manifest',
    productFooter:
      'Update availability, database and server software are not exposed by the API.',
    backups: 'Backups',
    backupsDetail:
      'The agent declares a backup provider. oui Observe does not take backups, and the API exposes no endpoint to read this one’s state — only the declaration above.',
    declaredCapabilities: 'Declared capabilities',
    declaredCapabilitiesSubtitle: '{count} ids, verbatim',
    capabilitiesUnavailable: 'Could not read this asset\u2019s capabilities. That is not the same as an asset which declares none.',
    noCapabilities: 'None. The manifest declares no capabilities at all.',
    capabilitiesFooter:
      'Present means supported; absent means unsupported. There is no third state, and nothing here is inferred from the product.',
  },

  capabilities: {
    stepped: 'stepped',
    mutates: 'mutates',
    reauth: 're-authentication',
    notModelled: 'not modelled here',
    needs: 'Needs {fields}',
    cancel: 'Cancel',
    notePlaceholder: 'Ticket, or reason',
    noteLabel: 'Note',
    manifestStale: 'manifest stale',
    reauthShort: 're-auth',
    declaredNotModelled: 'Declared, not modelled by this dashboard build',
    newerThanUi: 'This agent offers capabilities newer than this interface. They are dispatched with parameters exactly as entered; the core validates them against the manifest.',
    run: 'Run…',
    notDeclared: '{count} capability this asset does not declare | {count} capabilities this asset does not declare',
    manifestStaleTitle: 'The core believes this manifest is behind the agent’s current one. Actions still render; an operation against a capability that has since been switched off comes back blocked.',
    noteHelp: 'Recorded in the audit entry. Why, rather than what — the what is already in the operation record.',
    steppedTitle: 'Runs as a stepped operation with progress.',
    authorises: 'Authorises {action} once, for a few minutes.',
    confirm: 'Confirm',
    identityTitle: 'Confirm it is you',
    factorLabel: 'Factor',
    absentMeans: 'Absent means unsupported. There is no third state — the core never infers an ability from a product name, a version or a URL (protocol/capabilities.md §1).',
    actionsTitle: 'Actions',
    noOperationsTitle: 'This asset declares no operations.',
    reauthTitle: 'Requires a fresh authentication factor and writes an audit record (design §11.4).',
    noneDeclaredHint: 'Nothing here is broken. An agent may declare as little as core.info, and every capability is individually disableable from the site side (design §11.5). What it can report is shown in the panels below.',
    passwordLabel: 'Password',
    codeLabel: 'Code',
    reauthExplain: 'This changes something on a live asset. oui Observe asks for a fresh factor and records an audit entry.',
    panelTitle: 'Actions',
    noAgent: 'No agent on this asset',
    noAgentDetail:
      'This asset is watched from the outside: oui Observe measures its availability and response time without installing anything on it. An optional agent would add the extension inventory, updates, file scanning and the activity log — but none of that is needed for the availability monitoring above.',
    manifestUnavailable: 'No manifest received',
    manifestUnavailableDetail:
      'What this asset can do is unknown: no manifest has been fetched yet. That is not the same as a manifest which declares nothing.',
    noneDeclared: 'No actions declared',
    noneDeclaredDetail:
      'The agent answered, and its manifest declares no runnable capability. That is a complete answer, not a missing one.',
    requiresReauth: 'Requires re-authentication',
    destructive: 'Destructive action',
    confirmTitle: 'Confirm: {label}',
    confirmDetail: 'This action will run on {asset}.',
    dispatch: 'Run',
    dispatched: 'Operation queued',
    dispatchFailed: 'Could not queue the operation',
    renderedFrom: 'Rendered from {count} declared capabilities',
    runCapability: 'Run capability',
    confirmAndReauth: 'Confirm and re-authenticate',
    reauthLabel: '{action} on {asset}',
    invalidJson: 'The parameters are not valid JSON.',
    coreRefused: 'The core refused the operation.',
  },

  /**
   * The capability registry's words.
   *
   * Keyed by the CAPABILITY constant's camelCase name rather than by the capability id: `core.update`
   * has a dot in it, and a dot is a path separator here — `capabilityActions['core.update'].label`
   * would be read as four nested objects. `unmodelled` is the generic action synthesised for an id
   * this build does not model; its *label* is the humanised id itself and is deliberately absent
   * from this catalogue.
   */
  capabilityActions: {
    coreUpdate: {
      label: 'Update core',
      summary: 'Update the product itself to a named version, one step at a time.',
      confirm:
        'This changes files on the live site. It cannot be undone from here — a cancelled update is not a rolled-back update.',
      fields: {
        to_version: {
          label: 'Target version',
          placeholder: '5.2.2',
          help: 'An exact version. "latest" is never sent — the operation record must say what was actually intended.',
        },
        backup_first: {
          label: 'Ask the site to back up first',
          help: 'Honoured only if the agent has a backup provider. An agent without one ignores this rather than failing.',
        },
        allow_major: {
          label: 'Allow crossing a major version',
          help: 'With this off, a target across a major boundary comes back blocked instead of proceeding.',
        },
      },
    },

    coreChecksum: {
      label: 'Verify core files',
      summary: "Compare the product's files against the publisher's official checksums.",
      fields: {
        version: {
          label: 'Checksum manifest version',
          placeholder: 'installed version',
          help: 'Leave empty to compare against the installed version.',
        },
        include_unknown: {
          label: 'Report files not in the official manifest',
        },
      },
    },

    extensionsInstall: {
      label: 'Install extension',
      summary: 'Install an extension that is not currently present, from a verified URL.',
      confirm:
        'Installing code from a URL onto a live site is the highest-risk thing this protocol can do. Supply a checksum unless you have a reason not to.',
      fields: {
        source: {
          label: 'Source',
          options: {
            url: 'Download from a URL',
            directory: "The product's own directory",
          },
        },
        url: {
          label: 'Package URL',
          placeholder: 'https://…',
          help: 'Must be https. The agent refuses plaintext and refuses redirects that leave https.',
        },
        checksum: {
          label: 'Checksum',
          placeholder: 'sha256:…',
          help: 'When present the agent verifies it and fails the operation on mismatch.',
        },
        slug: {
          label: 'Expected identifier',
          help: 'The agent fails the operation if what it installed does not match this.',
        },
        enable: {
          label: 'Enable after installing',
        },
      },
    },

    filesScan: {
      label: 'Scan files',
      summary: "Detect additions, modifications and deletions across the site's file tree.",
      fields: {
        mode: {
          label: 'Mode',
          options: {
            incremental: 'Incremental — changes since the last scan',
            full: 'Full — re-hash everything',
            baseline: 'Baseline — record a new snapshot, report nothing',
          },
        },
        max_files: {
          label: 'File cap',
          placeholder: 'agent default',
          help: 'A safety cap for one operation.',
        },
      },
    },

    cacheClear: {
      label: 'Clear caches',
      summary: "Flush the product's caches. Safe, and the usual first move after an update.",
      fields: {
        scopes: {
          label: 'Scopes',
          placeholder: 'everything the agent controls',
          help: 'Comma-separated, e.g. page, data, opcode, cdn. Leave empty for everything.',
        },
      },
    },

    extensionsUpdate: {
      label: 'Update',
      summary: 'Update the selected extensions to the versions the agent reports as available.',
      confirm: 'This changes files on the live site.',
    },

    extensionsToggle: {
      label: 'Enable / disable',
      summary: 'Set an extension to a desired end state. Idempotent by construction.',
    },

    unmodelled: {
      summary:
        'Declared by this asset’s agent but not modelled by this dashboard build. Parameters are passed to the agent exactly as entered.',
      confirm:
        'This dashboard does not know what this capability does. The core will validate it against the manifest; nothing else here will.',
      fields: {
        __raw: {
          label: 'Parameters (JSON)',
          help: 'Sent as the operation’s params object, unchanged.',
        },
      },
    },
  },

  /** The read-only sections of the asset detail, each gated on one declared capability. */
  capabilityPanels: {
    core: {
      label: 'Product',
      summary: 'Version, update availability, PHP, database and server.',
    },
    extensions: {
      label: 'Extensions',
      summary: 'The installed inventory, with update and vulnerability state.',
    },
    admins: {
      label: 'Privileged accounts',
      summary: 'Who can change this asset.',
    },
    backup: {
      label: 'Backups',
      summary: 'What the asset says about its own backup arrangement.',
    },
    activity: {
      label: 'Activity',
      summary: 'Events this asset pushes to the core.',
    },
    integrity: {
      label: 'File integrity',
      summary: 'Findings from file scans and core checksum verification.',
    },
  },

  operations: {
    title: 'Operations',
    subtitle:
      'What oui Observe asked agents to do, and what they reported back. Every operation carries its own id, from the button to the log.',
    empty: 'No operations',
    emptyDetail:
      'No operation matches these filters. Operations appear here as soon as an action is dispatched.',
    loadError: 'Could not load operations',
    countLabel: 'operations',
    capability: 'Capability',
    state: 'State',
    progress: 'Progress',
    started: 'Started',
    finished: 'Finished',
    attempt: 'Attempt {count}',
    retry: 'Retry',
    cancel: 'Cancel operation',
    blockedReason: 'Blocked because',
    result: 'Result',
    requestedBy: 'Requested by',
    noProgress: 'The agent reports no progress for this operation.',
    filters: {
      state: 'State',
      capability: 'Capability',
      asset: 'Asset',
      active: 'In flight only',
    },
    explainQueued: 'Accepted by the core, not yet dispatched to the agent.',
    explainRunning: 'The agent is working; the core is driving the step loop.',
    explainDone: 'Finished successfully.',
    explainError: 'The operation failed.',
    explainBlocked: 'The asset refused to proceed.',
    progressLabel: '{capability} progress',
    elapsed: '{duration} elapsed',
    blockedPolicy: 'Blocked is terminal. The core will not retry it, and neither will this dashboard — re-running it before the underlying condition changes just produces the same answer.',
    blockedUnknown: 'The asset refused for a reason the core does not model.',
    notRetryable: 'The agent reported this as not worth retrying. Retrying anyway would produce the same failure; the cause is on the asset.',
    requestedByLine: 'requested by {name}',
    reauthenticated: 're-authenticated',
    blocked: {
      MAINTENANCE_MODE: 'The site is offline or in maintenance and refuses changes.',
      CAPABILITY_DISABLED: "This capability has been switched off in the agent's own configuration.",
      INSUFFICIENT_PRIVILEGES: "The agent's account or process does not have the rights.",
      FILESYSTEM_READONLY: 'The install is on read-only media or an immutable deployment.',
      ANOTHER_OPERATION_RUNNING: 'Another operation holds the lock on this asset. Try again after it.',
      LICENSE_REQUIRED: 'The target needs a subscription or licence key the agent does not hold.',
      USER_INTERVENTION_REQUIRED: 'Somebody has to act on the site itself first.',
      PRECONDITION_FAILED: 'A stated precondition of this capability is not met.',
    },
  },

  activity: {
    title: 'Activity',
    subtitle:
      'Events pushed from monitored assets to the core. Push, not pull — an attacker who owns a site can wipe its local logs, but not logs that have already left it.',
    volume: 'Volume',
    volumeSubtitle: 'From the hourly continuous aggregate, not from the events table',
    loadingChart: 'Loading chart',
    searching: 'Searching',
    empty: 'No events',
    emptyDetail:
      'No event matches these filters in this window. Widen the range or remove a filter.',
    countLabel: 'events',
    occurredAt: 'Occurred',
    asset: 'Asset',
    category: 'Category',
    action: 'Action',
    severityCol: 'Severity',
    actor: 'Actor',
    target: 'Target',
    sourceIp: 'Source IP',
    payload: 'Payload',
    filters: {
      search: 'Search the log...',
      actor: 'Actor',
      action: 'Action',
      category: 'Category',
      severityMin: 'Minimum severity',
      groupBy: 'Group by',
      groupNone: 'No grouping',
      groupCategory: 'Category',
      groupAction: 'Action',
      groupAsset: 'Asset',
      reset: 'Reset filters',
      actorAria: 'Filter by actor',
      actionAria: 'Filter by action',
      severityAny: 'Any severity',
      severityAndAbove: '{severity} and above',
      severityCriticalOnly: 'critical only',
      groupChartCategory: 'Group the chart by category',
      groupChartSeverity: 'Group the chart by severity',
      groupChartAsset: 'Group the chart by asset',
      groupChartTotal: 'A single series, the total',
      notDeclared: 'This asset\'s agent does not declare that it emits this category.',
      coverageDeclares: 'This asset declares',
      coverageDetail: 'Categories absent from this list are flagged; an empty result for one of them is a coverage gap, not a quiet day.',
    },
    emptyDetailNoPush: 'No event matches these criteria. An asset that never declared logs.push sends none: that silence is a capability gap, not a quiet day. Widen the range or drop a filter.',
    tableCaption: 'Activity log events, most recent first.',
    eventId: 'Event id',
    operation: 'Operation',
    userAgent: 'User agent',
  },

  alerts: {
    title: 'Alerts',
    subtitle:
      'One rule engine for every kind of asset. A rule is evaluated against facts; a rule requiring a fact nothing reports will never fire, and says so.',
    empty: 'No rules',
    emptyDetail: 'No alert rule is defined yet. Create one to be notified.',
    loadError: 'Could not load rules',
    newRule: 'New rule',
    editRule: 'Edit rule',
    deleteRule: 'Delete rule',
    ruleLabel: 'Rule name',
    scope: 'Scope',
    scopeAll: 'The whole estate',
    scopeGroup: 'A group',
    scopeAsset: 'One asset',
    condition: 'Condition',
    severityField: 'Severity',
    channels: 'Channels',
    throttle: 'Throttle',
    throttleHelp: 'Minimum delay between two notifications for this rule, in seconds.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    requiredFacts: 'Required facts',
    requiredFactsHelp:
      'This rule needs these facts. An asset that reports none of them will never fire this rule.',
    saved: 'Rule saved',
    deleted: 'Rule deleted',
    saveFailed: 'Could not save the rule',
    conditionBuilder: {
      all: 'All of',
      any: 'Any of',
      not: 'Not',
      always: 'Always',
      fact: 'Fact',
      operator: 'Operator',
      value: 'Value',
      addCondition: 'Add a condition',
      addGroup: 'Add a group',
      remove: 'Remove',
      joinAll: 'and',
      joinAny: 'or',
      negated: 'not ({condition})',
      fireWhen: 'Fire when',
      allMatch: 'every condition is met',
      anyMatch: 'at least one condition is met',
      removeCondition: 'Remove this condition',
      valueFor: 'Value for {fact}',
      commaSeparated: 'comma separated',
      noValueNeeded: 'asks about what we know: no value is needed',
      operators: {
        eq: 'is',
        ne: 'is not',
        lt: 'is less than',
        lte: 'is at most',
        gt: 'is greater than',
        gte: 'is at least',
        in: 'is one of',
        not_in: 'is not one of',
        contains: 'contains',
        starts_with: 'starts with',
        ends_with: 'ends with',
        exists: 'is known',
        missing: 'is unknown',
      },
      facts: {
        availabilityState: 'Availability state',
        availabilityStateHelp: 'Only "down" and "recovering" are reached after quorum across two cycles (design §9.3).',
        healthState: 'Health',
        responseTime: 'Response time',
        cpu: 'CPU',
        memory: 'Memory',
        disk: 'Disk',
        tlsDaysLeft: 'Days left on the TLS certificate',
        coreUpdateAvailable: 'Product update available',
        coreUpdateSeverity: 'Product update severity',
        coreUpdateSeverityHelp: 'An open value; "security" is the one worth alerting on.',
        extensionsOutdated: 'Outdated extensions',
        extensionsVulnerable: 'Vulnerable extensions',
        eventCategory: 'Event category',
        eventAction: 'Event action',
        eventSeverity: 'Event severity',
        availabilityRecovered: 'Asset recovered',
      availabilityRecoveredHelp:
        'True at the moment the asset answers again after an outage. Fires once, on recovery — unlike "Availability state", which stays true for the whole outage.',
      availabilityOutageStarted: 'Outage started',
      availabilityOutageStartedHelp:
        'True at the moment a quorum concludes the asset is down. Once per incident.',
      availabilityChanged: 'State changed',
      availabilityChangedHelp: 'True on any availability transition, in either direction.',
      availabilityNotifies: 'Transition worth notifying',
      availabilityNotifiesHelp:
        'The quorum\u2019s own conclusion that this transition deserves a notification. Filters the flapping a single probe causes.',
      agentSilentHours: 'Hours since the agent last pushed',
        agentSilentHoursHelp: 'An asset that pushed daily for months and then went quiet may have had its agent disabled (design §13).',
        backupConfigured: 'Backup configured',
        superUserCount: 'Super-user count',
      },
    },
    quorumIntro: 'One rules engine and one notification path for every asset kind. A single failed probe never raises an alert — an asset is down only when a quorum of probers agree, across two consecutive cycles.',
    factsBanner: 'No rule depends on facts that must be reported first. | One rule depends on facts that must be reported before it can ever fire. | {count} rules depend on facts that must be reported before they can ever fire.',
    factsBannerDetail: 'A rule whose facts nothing produces is not a quiet rule, it is a broken one, and the day somebody notices is usually the day it should have fired.',
    loadingRules: 'Loading rules',
    createFirstRule: 'Create the first rule',
    tableCaption: 'Alert rules, their scope, condition and channels.',
    toggleRule: 'Enable or disable the “{label}” rule',
    needsFacts: 'needs {facts}',
    scopeValue: '{type}: {value}',
    ruleCount: 'no rules | one rule | {count} rules',
    enabledCount: 'none enabled | one enabled | {count} enabled',
    columns: {
      on: 'On',
      rule: 'Rule',
      created: 'Created',
    },
    scopeType: {
      group: 'Group',
      asset: 'Asset',
      kind: 'Kind',
      tag: 'Tag',
    },
    scopeKind: 'Every asset of a kind',
    scopeTag: 'Every asset carrying a tag',
    scopeWhich: 'Target',
    labelPlaceholder: 'Disk pressure on production servers',
    appliesTo: 'Applies to',
    appliesToHelp: 'One rule shape covers every asset kind — which is the whole point of alerting on the asset rather than on a particular type.',
    chooseGroup: 'Choose a group',
    chooseAsset: 'Choose an asset',
    chooseKind: 'Choose a kind',
    tagPlaceholder: 'tag',
    conditionHelp: 'Stored as a declarative tree: comparable in an audit entry, and renderable as a form.',
    severityHelp: 'A label here, an integer on events. Both are intended; the core maps between them.',
    throttleRationale: 'An alert that repeats every minute is an alert that ends up in a folder.',
    channelsRequired: 'Please choose at least one.',
    saveChanges: 'Save changes',
    createRule: 'Create rule',
    throttleEvery: 'Every time it fires',
    throttle15m: 'At most every 15 minutes',
    throttleHourly: 'At most once an hour',
    throttle6h: 'At most every 6 hours',
    throttleDaily: 'At most once a day',
    channel: {
      email: 'Email',
      webpush: 'Push notification',
      webhook: 'Webhook',
      slack: 'Slack',
      'google-chat': 'Google Chat',
      'oui-send': 'oui Send',
      intervention: 'Intervention (ticket)',
    },
  },

  extensions: {
    title: 'Extensions',
    name: 'Name',
    type: 'Type',
    version: 'Version',
    available: 'Available',
    status: 'Status',
    empty: 'No extensions',
    emptyDetail: 'The agent answered, and the inventory is empty.',
    updateAvailable: 'Update available',
    securityUpdate: 'Security update',
    vulnerable: 'Vulnerable',
    vulnerableHint: 'Possibly vulnerable',
    upToDate: 'Up to date',
    unknownState: 'Unknown — the agent did not say whether an update exists',
    enabled: 'Enabled',
    disabled: 'Disabled',
    protectedExtension: 'Protected',
    checkedAt: 'Checked {at}',
    neverChecked: 'Never checked',
    selected:
      'no extensions selected | one extension selected | {count} extensions selected',
    update: 'Update',
    updateAll: 'Update all',
    extension: 'Extension',
    noMatch: 'Nothing matches this filter',
    unknownHint: 'No update version was reported. That means nobody knows — not that it is current.',
    advisoriesCheckedAt: 'Known advisories — last checked {at}',
    searchPlaceholder: 'Filter by name, slug or type...',
    tableCaption: 'Installed extensions, with update and vulnerability state.',
    installed: 'Installed',
    securityBadge: 'Security',
    unknownBadge: 'Unknown',
    securityHint: 'The agent reported this update as a security release.',
    advisories: 'Known advisories',
    protectedHint: 'Shipped with the product, or not uninstallable.',
    enable: 'Enable',
    disable: 'Disable',
    selectOne: 'Select {name}',
    selectUpdatable: 'Select every updatable one',
    updateSelected: 'Update selection',
    updatableCount: 'no update available | one extension to update | {count} extensions to update',
    noMatchDetail: 'No extension matches this filter. Choose "All" to see the whole inventory again.',
    unknownCaveat: '{count} of these report no available version. That is unknown, not "up to date": the agent did not say, and {unknown} is the honest answer.',
    reauthUpdate: 'Update one extension on {asset} | Update {count} extensions on {asset}',
    updating: 'Updating one extension | Updating {count} extensions',
    reauthEnable: 'Enable {name} on {asset}',
    reauthDisable: 'Disable {name} on {asset}',
    filters: {
      all: 'All',
      outdated: 'Outdated',
      vulnerable: 'Vulnerable',
      disabled: 'Disabled',
      updateUnknown: 'Update unknown',
    },
  },

  charts: {
    tableView: 'Show table',
    chartView: 'Show chart',
    tableCaption: 'Data for the “{label}” chart',
    legend: 'Legend',
    noSample: 'no sample',
    gap: 'No sample in this interval',
    total: 'Total',

    noDataYet: 'No measurements yet',
    probeWaiting: 'Probe waiting for its first cycle',
    probeWaitingDetail:
      'This asset is registered, but no probe cycle has completed yet. The first points will appear here as soon as the next cycle finishes.',
    noDataInWindow: 'No measurements in this window',
    noDataInWindowDetail:
      'Nothing was measured between {from} and {to}. Widen the range to look further back.',
    cannotReport: 'This asset cannot report this measurement',
    cannotReportDetail:
      'This measurement comes from an agent installed on the asset. This one is watched from the outside: its availability and response time are measured, the rest is not.',
    loadFailed: 'Could not load measurements',

    uptimeTimeline: 'Availability timeline',
    uptimeTimelineSubtitle: 'One cell per probe interval, oldest to newest',
    uptimeLegendUp: 'Up',
    uptimeLegendDown: 'Down',
    uptimeLegendUnknown: 'No sample',
    uptimeCell: '{at} — {state}',
    uptimeNoWindows: 'No availability history',
    uptimeNoWindowsDetail:
      'No prober has reached a verdict in this window. That is not 100% uptime, and it is not 0%.',

    availabilityMeter: 'Uptime',
    availabilityUnknown: 'Uptime unknown',
    availabilityUnknownDetail:
      'No prober has measured this asset in the window. An unknown ratio is not a ratio of 100%.',
    outagesCount: 'no outages | one outage | {count} outages',

    responseTime: 'Response time',
    responseTimeSubtitle: 'Mean per interval, measured from the probers',

    activityVolume: 'Activity volume',
    eventsPerBucket: 'Events per bucket',
    bucketsOf: '{bucket} buckets',
    dimension: {
      category: 'category',
      action: 'action',
      asset: 'asset',
    },
    groupedBy: 'by {dimension}',
    eventCount: 'no events | one event | {count} events',
    severeCount: '{count} severe',
    noEventsInWindow: 'No events in this window',
    noEventsInWindowDetail:
      'Nothing was pushed between {from} and {to}. Events arrive from agents installed on assets; an asset with no agent pushes none.',
    sharedScale: 'Shared scale across all panels',
    othersFolded: 'Categories beyond the first eight are folded into “Other”.',
  },

  metrics: {
    http_response_ms: 'Response time',
    http_up: 'Probe response',
    http_status: 'HTTP status',
    content_bytes: 'Response size',
    dns_record_count: 'DNS records',
    header_leak_count: 'Revealing headers',
    header_missing_count: 'Missing security headers',
    port_open_count: 'Open ports',
    port_unexpected_open_count: 'Unexpected open ports',
    rdap_days_left: 'Domain expiry',
    tls_chain_length: 'TLS chain length',
    tls_days_left: 'TLS certificate expiry',
    cpu_pct: 'CPU',
    mem_pct: 'Memory',
    disk_pct: 'Disk',
    swap_pct: 'Swap',
    inode_pct: 'Inodes',
    load_1m: 'Load (1m)',
    net_rx_bytes: 'Network in',
    net_tx_bytes: 'Network out',
    proc_count: 'Processes',
  },

  pagination: {
    noResults: 'No {label}',
    range: '{first}–{last} of {total} {label}',
    rangeUnknown: '{label} {first}–{last}',
    page: 'Page {page} of {pages}',
    first: 'First page',
    previous: 'Previous page',
    next: 'Next page',
    last: 'Last page',
    estimateHint: 'Estimated from the query planner, not an exact count.',
    defaultLabel: 'results',
  },

  notFound: {
    title: 'Page not found',
    detail: 'That address does not match any oui Observe screen.',
    back: 'Back to assets',
  },

  errors: {
    archiveAsset: 'Could not archive',
    saveAsset: 'Could not save the asset',
    mintToken: 'Could not generate the token',
    UNAUTHORIZED: 'Your session has expired. Please sign in again.',
    FORBIDDEN: 'You are not permitted to do that.',
    MFA_REQUIRED: 'A second factor is required to continue.',
    REAUTHENTICATION_REQUIRED:
      'This action is destructive: confirm your identity to dispatch it.',
    CAPABILITY_UNSUPPORTED: 'This asset does not declare that capability.',
    CAPABILITY_DISABLED: 'That capability is declared but disabled on this asset.',
    MANIFEST_UNAVAILABLE:
      'No manifest has been fetched yet, so what this asset can do is unknown.',
    ASSET_ARCHIVED: 'This asset is archived. Restore it to act on it.',
    ASSET_HAS_NO_AGENT:
      'This asset has no agent: it is watched from the outside and cannot run operations.',
    OP_CONFLICT: 'Another operation is already in flight on this asset.',
    ILLEGAL_STATE: 'The current state does not allow that.',
    RATE_LIMITED: 'Too many requests. Please wait before retrying.',
    RATE_LIMITED_RETRY: 'Too many requests. Please retry in {seconds} seconds.',
    INVALID_PARAMS: 'The request is invalid. Check the highlighted fields.',
    NOT_FOUND: 'Not found.',
    NOT_IMPLEMENTED: 'That is not implemented in the core yet.',
    INTERNAL: 'The core reported an internal error.',
    UNAVAILABLE: 'The service is temporarily unavailable.',

    network: 'Could not reach the oui Observe core. Check your network connection.',
    unrecognised: 'The core refused the request ({code}).',
    unknown: 'Something went wrong.',

    loadAssets: 'Could not load assets',
    loadAsset: 'Could not load this asset',
    loadMetrics: 'Could not load metrics',
    loadUptime: 'Could not load uptime',
    loadEvents: 'Could not search the log',
    loadStatistics: 'Could not load statistics',
    loadOperations: 'Could not load operations',
    loadExtensions: 'Could not load the inventory',
    loadAlerts: 'Could not load rules',
    loadManifest: 'Could not load the manifest',
  },
  ui: {
    progressLabel: 'Operation progress',
    progressUnknown: 'in progress, amount unknown',
    dismissToast: 'Dismiss',
  },
} as const
